import { CONTRACTABLE_BUFFER, errorMessages } from "@/lib/constants";
import {
  CAMPAIGN_CODE_ERROR_REASONS,
  SUBSCRIPTION_ESTIMATE_ERROR_MESSAGES,
  VOUCHER_CODE_CHECK_ERROR_REASONS,
} from "@/lib/errors";
import { changePaymentInstrument } from "@/lib/mailTemplate";
import sendEmail from "@/lib/sendEmail";
import { gql } from "@apollo/client";
import { composeResolvers } from "@graphql-tools/resolvers-composition";
import {
  dateFormat,
  isAuthenticated,
  isDateRangeIn,
  logicaErrorBody,
  logicaErrorCode,
  ownerOnly,
  sleep,
} from "./helper";

export const typeDef = gql`
  type PaymentInstrumentValues {
    card_number: String
    card_type: String
  }
  type PaymentInstrument {
    values: PaymentInstrumentValues
  }

  type Entitlement {
    right_id: Int
  }

  type Subscription @cacheControl(scope: PRIVATE, maxAge: 0) {
    subscription_id: ID
    course_id: Int
    payment_method_id: Int
    payment_instrument_id: Int
    initialTransactionAt: String
    nextTransactionAt: String
    status: String
    course: Course
    payment_instrument: PaymentInstrument
    withdraw_at: String
    trialStartAt: String
    trialEndAt: String
    entitlements: [Entitlement]
    suspend_flag: Boolean
    init_order_id: Int
    orderPrice: Int
    error: String
  }

  type OptionsValue {
    label: String
    value: String
  }
  type FormQuestions {
    type: String
    label: String
    unique: String
    help_text: String
    options: [OptionsValue]
    key_name: String
    required: Boolean
    schema_group_name: String
  }

  type FormQuestionsResponse {
    question_id: Int
    data: [FormQuestions]
    values: JSON
  }

  type SubscriptionCancel @cacheControl(scope: PRIVATE) {
    course_id: Int
    status: String
    withdraw_at: String
    error: String
  }

  type FormQuestionAnswer {
    answer_id: Int
  }

  type SubscriptionCheckCancelableResponse {
    cancelable: Boolean
    subscription: SubscriptionCheckCancelableSubscription
  }

  type SubscriptionCheckCancelableSubscription {
    withdraw_at: String
  }

  type SubscriptionChangePaymentResult {
    payment_instrument_id: Int
    error: String
  }

  type SubscriptionStatusResponse @cacheControl(scope: PRIVATE, maxAge: 0) {
    is_contractable: Boolean
    is_contracted: Boolean
    have_be_contracted: Boolean
    is_trial_enabled: Boolean
    subscription: Subscription
  }

  type SubscriptionEstimate @cacheControl(scope: PRIVATE, maxAge: 0) {
    subscription: Subscription
    voucherCode: VoucherCode
    coursePricings: [CoursePrice]
    paymentPrice: CoursePrice
    # broadcastDiscountCampaignPrice: CoursePrice
    campaignPrice: CoursePrice
    error: String
  }

  type Query {
    subscriptions: [Subscription]

    subscriptionOne(id: Int): Subscription

    formQuestions(id: Int): FormQuestionsResponse @cacheControl(maxAge: 0)

    subscriptionCheckCancelable(
      subscriptionId: Int
    ): SubscriptionCheckCancelableResponse

    subscriptionStatus(courseId: Int): SubscriptionStatusResponse

    unSubscribedStatus(courseId: Int): Subscription

    subscriptionEstimate(
      courseId: Int
      paymentMethodId: Int
      couponCode: String
    ): SubscriptionEstimate
  }

  type Mutation {
    subscribe(
      courseId: Int
      paymentInstrumentId: Int
      voucherCode: String
      campaignCode: String
      valuePlanTerm: String
    ): Subscription

    subscriptionCancel(id: Int): SubscriptionCancel

    subscriptionChangePayment(
      paymentInstrumentId: Int!
      subscriptionId: Int!
    ): SubscriptionChangePaymentResult

    formQuestionAnswer(values: String, id: Int): FormQuestionAnswer
  }
`;

export const resolvers = composeResolvers(
  {
    Query: {
      subscriptions: async (_parent, _args, { req, dataSources }) => {
        const result = await dataSources.subscribe.subscription();
        const subscriptions = result?.subscriptions?.filter(
          (data) =>
            data?.course?.values?.adult_flag === false &&
            data?.entitlements &&
            data?.entitlements?.length !== 0
        );
        return subscriptions;
      },
      subscriptionOne: async (_parent, { id }, { dataSources }) => {
        const res = await dataSources.subscribe.subscriptionOne(id);
        return res.subscription;
      },
      formQuestions: async (_parent, args, { dataSources }) => {
        const res = await dataSources.subscribe.formQuestions(args);
        return {
          question_id: res?.question?.question_id,
          data: res?.question?.schema,
          values: res?.question?.values,
        };
      },
      subscriptionCheckCancelable: async (
        _parent,
        { subscriptionId },
        { dataSources }
      ) => {
        try {
          const res = await dataSources.subscribe.subscriptionCheckCancelable(
            subscriptionId
          );
          return res[subscriptionId];
        } catch (e) {
          console.log("subscribe check cancelable error", e);
        }
      },
      subscriptionStatus: async (_parent, { courseId }, { dataSources }) => {
        try {
          const res = await dataSources.subscribe.subscriptionStatus(courseId);
          const _subscription = res.subscriptions?.[0];
          const getSubscription = async (subscriptionId) => {
            if (subscriptionId) {
              const subscription = await dataSources.subscribe.subscriptionOne(
                subscriptionId
              );
              return subscription?.subscription;
            } else {
              return null;
            }
          };
          const subscription = await getSubscription(
            _subscription?.subscription_id
          );
          return { ...res, subscription: subscription };
        } catch (e) {
          console.log("subscriptionStatus error", e);
        }
      },
      unSubscribedStatus: async (_parent, { courseId }, { dataSources }) => {
        try {
          const res = await dataSources.subscribe.subscriptionStatus(courseId);
          const _subscription = res.subscriptions?.[0];
          const getSubscription = async (subscriptionId) => {
            if (subscriptionId) {
              const subscription = await dataSources.subscribe.subscriptionOne(
                subscriptionId
              );
              return subscription?.subscription;
            } else {
              return null;
            }
          };
          const subscription = await getSubscription(
            _subscription?.subscription_id
          );
          if (subscription) {
            console.log("while subscribing");
            return null;
          } else {
            return res?.subscriptions[0];
          }
        } catch (e) {
          console.log("unSubscribedStatus error", e);
        }
      },
      subscriptionEstimate: async (
        _parent,
        { courseId, paymentMethodId, couponCode },
        { dataSources }
      ) => {
        try {
          const res = await dataSources.subscribe.estimate(
            courseId,
            paymentMethodId,
            couponCode
          );
          return {
            subscription: res?.subscription,
            voucherCode: res?.voucher_code,
            coursePricings: res?.course_pricings,
            paymentPrice: res?.payment_pricing,
            campaigns: res?.campaigns,
          };
        } catch (e) {
          console.log("subscriptionEstimate error", e);
          const body = logicaErrorBody(e);
          const errorCode = body?.error?.code;
          const reason = body?.error?.reason_type;
          const { code, message } = (() => {
            if (
              errorCode === 22001 &&
              reason &&
              VOUCHER_CODE_CHECK_ERROR_REASONS[reason]
            ) {
              return VOUCHER_CODE_CHECK_ERROR_REASONS[reason];
            } else if (
              errorCode === 34001 &&
              reason &&
              CAMPAIGN_CODE_ERROR_REASONS[reason]
            ) {
              return CAMPAIGN_CODE_ERROR_REASONS[reason];
            }
            return {
              code: errorCode,
              message:
                SUBSCRIPTION_ESTIMATE_ERROR_MESSAGES[errorCode] ||
                errorMessages["subscriptionEstimate"],
            };
          })();
          return {
            error: `${message} エラーコード: ${code}`,
          };
        }
      },
    },
    Mutation: {
      subscribe: async (
        _parent,
        {
          courseId,
          paymentInstrumentId,
          voucherCode,
          campaignCode,
          valuePlanTerm,
        },
        { req, dataSources }
      ) => {
        try {
          // コースを取得して契約期間を確認
          const course = await dataSources.courses.courseById(courseId);
          const contractAllowed = isDateRangeIn(
            course?.contractable_start_at,
            course?.contractable_end_at,
            { buffer: CONTRACTABLE_BUFFER, default: true }
          );
          if (!contractAllowed) {
            return {
              error: "契約可能期間外のため購入できません。",
            };
          }

          // キャンペーンコードがある場合は、applyAPIをコール
          if (campaignCode) {
            console.log(`apply couponCode: ${campaignCode}`);
            await dataSources.subscribe.applyCampaign(courseId, campaignCode);
          }

          const res = await dataSources.subscribe.subscribe(
            courseId,
            paymentInstrumentId,
            voucherCode
          );

          const userId = req?.userToken?.id;
          console.log(
            `########## Subscribe, userId:${userId}, courseId: ${courseId} ##########`
          );

          console.log("subscribe", res);
          console.log("########## Subscribe End ##########");
          await sleep(200);
          // バリュープランだったら規約に同意させる
          if (valuePlanTerm) {
            await dataSources.terms.agree(valuePlanTerm);
          }

          const orderId = res.subscription.init_order_id;
          let orderData = {};
          if (!!orderId) {
            orderData = await dataSources.orders.getOrderOne(orderId);
          }
          const orderPrice = orderData?.payment_amount || 0;
          const subscription = res.subscription;
          // TODO: トライアルなしオーダー（init_order_id）が存在しない場合のエラーログ、通知を追加する
          return { ...subscription, orderPrice };
        } catch (e) {
          console.log("subscribe error", e);
          const errorCode = logicaErrorCode(e);
          return {
            error: `${errorMessages["subscribeExecute"]} エラーコード: ${errorCode}`,
          };
        }
      },
      subscriptionCancel: async (_parent, { id }, { req, dataSources }) => {
        try {
          const res = await dataSources.subscribe.subscriptionCancel(id);

          const userId = req?.userToken?.id;
          console.log(
            `########## subscriptionCancel, userId:${userId}, courseId: ${id} ##########`
          );
          console.log("subscriptionCancel", res);
          return res.subscription;
        } catch (e) {
          console.log("subscribe cancel error", e);
          const message = e?.extensions?.response?.body?.error?.message;
          const errorCode = logicaErrorCode(e);

          return { error: `${message} エラーコード: ${errorCode}` };
        }
      },
      formQuestionAnswer: async (_parent, { id, values }, { dataSources }) => {
        try {
          const res = await dataSources.subscribe.formQuestionAnswer(
            id,
            values
          );
          return res.answers;
        } catch (e) {
          console.log("form question answer error", e);
        }
      },
      subscriptionChangePayment: async (
        _parent,
        args,
        { req, dataSources }
      ) => {
        try {
          const {
            subscription,
          } = await dataSources.subscribe.subscriptionChangePayment(args);
          const {
            payment_instrument: paymentInstrument,
          } = await dataSources.paymentInstruments.paymentInstrument(
            subscription.payment_instrument_id
          );
          const course = await dataSources.courses.courseById(
            subscription.course_id
          );
          // メール送信
          const { user } = await dataSources.user.me();
          await sendEmail(
            changePaymentInstrument({
              address: user.mail_address,
              paymentInstrument,
              course,
            })
          );
          return subscription;
        } catch (e) {
          console.log("subscribe change payment error", e);
          const errorCode = logicaErrorCode(e);
          return {
            error: `${errorMessages["subscriptionChangePayment"]} エラーコード: ${errorCode}`,
          };
        }
      },
    },
    Subscription: {
      initialTransactionAt: (sub) => dateFormat(sub.initial_transaction_at),
      nextTransactionAt: (sub) => dateFormat(sub.next_transaction_at),
      trialStartAt: (sub) => dateFormat(sub.trial_start_at),
      trialEndAt: (sub) => dateFormat(sub.trial_end_at),
    },
    SubscriptionEstimate: {
      // broadcastDiscountCampaignPrice: ({ coursePricings, campaigns }) => {
      //   // TODO: 本番のAPIが更新されてcourse_pricingsにcampaign情報が入るようになったらcoursePricingsで直接見る
      //   // 設定されている放送契約割引キャンペーン
      //   if (!coursePricings && !campaigns) return null;
      //   const campaign = availableCamapigns(coursePricings, campaigns).find(
      //     (campaign) => campaign?.values?.is_broadcast_discount
      //   );
      //   console.log(coursePricings, campaigns);
      //   if (!campaign) return null;
      //   const price = coursePricings.find((cp) => {
      //     const campaignId = cp?.campaign_id || cp?.campaign?.campaign_id;
      //     return Number(campaignId) === Number(campaign?.campaign_id);
      //   });
      //   return price;
      // },
      campaignPrice: ({ paymentPrice }) => {
        // 適用された放送契約割引ではないキャンペーン
        if (
          paymentPrice?.campaign &&
          !paymentPrice?.campaign?.values?.is_broadcast_discount
        ) {
          return paymentPrice;
        }
        return null;
      },
    },
  },
  {
    "Mutation.subscribe": [isAuthenticated(), ownerOnly()],
    "Mutation.subscriptionCancel": [isAuthenticated(), ownerOnly()],
    "Mutation.subscriptionChangePayment": [isAuthenticated(), ownerOnly()],
    "Mutation.formQuestionAnswer": [isAuthenticated(), ownerOnly()],
  }
);

/**
 * availableCamapigns.
 * estimateAPIの仕様変更対応
 * APIのレスポンスからcampaignsが廃止され、course_pricingsにcampaignが含まれるようになる
 * APIリリース時にエラーにならないようにどちらの場合でも動作するようにする
 * 後に新しい仕様に寄せる
 *
 * @param {} course_pricings
 * @param {} campaigns
 */
function availableCamapigns(coursePricings, campaigns) {
  if (campaigns) return campaigns;
  if (!coursePricings) return [];
  return coursePricings.filter((cp) => cp.campaign).map((cp) => cp.campaign);
}
